import { useEffect, useState } from "react";
import {
  Page,
  Card,
  IndexTable,
  Text,
  Modal,
  FormLayout,
  TextField,
  Checkbox,
  Banner,
  BlockStack,
  InlineStack,
  Button,
  Badge,
} from "@shopify/polaris";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import ClabeBadge from "../Services/ClabeBadge";
import { clabeBankName, isValidClabe, clabeDigits } from "../../utility/clabe";
import { formatDate } from "../../utility/funcs";

// Trasladistas: the drivers who move our units between cities. Not service
// providers (those are businesses that invoice us). Their CLABE is verified
// with Fintoc through the same pipeline as providers: saving a CLABE queues a
// bank_account_verifications row and pagos-svc checks the holder with the bank.
// The badge reads trasladistas_bank_status, derived live from that row.

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  rfc: "",
  curp: "",
  license_number: "",
  license_expires: "",
  clabe: "",
  active: true,
  notes: "",
};

function Trasladistas() {
  const { hasPermission } = UserAuth();
  const canEdit = hasPermission("services.create");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, ...fields}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: people }, { data: statuses }, { data: counts }] = await Promise.all([
      supabase.from("trasladistas").select("*").order("name"),
      supabase.from("trasladistas_bank_status").select("*"),
      supabase.from("traslado_orders").select("trasladista").not("trasladista", "is", null),
    ]);
    const byId = Object.fromEntries((statuses || []).map((s) => [s.trasladista_id, s]));
    const trips = {};
    for (const c of counts || []) trips[c.trasladista] = (trips[c.trasladista] || 0) + 1;
    setRows((people || []).map((p) => ({ ...p, bank: byId[p.id] || {}, trips: trips[p.id] || 0 })));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  // A verification takes ~1–2 minutes; refresh while any is in flight.
  useEffect(() => {
    if (!rows.some((r) => r.bank.clabe_status === "pending")) return;
    const t = setTimeout(load, 15000);
    return () => clearTimeout(t);
  }, [rows]);

  const set = (field) => (value) => setEditing((prev) => ({ ...prev, [field]: value }));

  const clabe = clabeDigits(editing?.clabe || "");
  const clabeError =
    editing && clabe && !isValidClabe(clabe) ? "CLABE inválida (18 dígitos con dígito verificador)" : null;
  const expiredLicense =
    editing?.license_expires && new Date(editing.license_expires) < new Date();

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      name: editing.name.trim(),
      phone: editing.phone.trim() || null,
      email: editing.email.trim() || null,
      rfc: editing.rfc.trim().toUpperCase() || null,
      curp: editing.curp.trim().toUpperCase() || null,
      license_number: editing.license_number.trim() || null,
      license_expires: editing.license_expires || null,
      clabe: clabe || null,
      banco: clabe ? clabeBankName(clabe) : null,
      active: editing.active,
      notes: editing.notes.trim() || null,
    };
    const q = editing.id
      ? supabase.from("trasladistas").update(payload).eq("id", editing.id)
      : supabase.from("trasladistas").insert(payload);
    const { error: err } = await q;
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setEditing(null);
    load();
  };

  const reverify = async (id) => {
    const { error: err } = await supabase.rpc("request_trasladista_verification", {
      p_trasladista: id,
    });
    if (err) setError(err.message);
    load();
  };

  const visible = rows.filter((r) => showInactive || r.active);
  const editingRow = editing?.id ? rows.find((r) => r.id === editing.id) : null;

  return (
    <AppWrapper>
      <Page
        title="Trasladistas"
        subtitle="Choferes que mueven las unidades; su cuenta se verifica con el banco (Fintoc)"
        backAction={{ url: "/vehicle_transfers" }}
        primaryAction={{
          content: "Nuevo trasladista",
          disabled: !canEdit,
          onAction: () => {
            setError(null);
            setEditing({ ...EMPTY });
          },
        }}
      >
        <BlockStack gap="300">
          {error && !editing && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}
          <InlineStack>
            <Checkbox label="Mostrar inactivos" checked={showInactive} onChange={setShowInactive} />
          </InlineStack>
          <Card padding="0">
            <IndexTable
              resourceName={{ singular: "trasladista", plural: "trasladistas" }}
              itemCount={visible.length}
              loading={loading}
              selectable={false}
              headings={[
                { title: "Nombre" },
                { title: "Teléfono" },
                { title: "Licencia" },
                { title: "Banco" },
                { title: "Cuenta" },
                { title: "Traslados", alignment: "end" },
              ]}
            >
              {visible.map((r, i) => {
                const licExpired = r.license_expires && new Date(r.license_expires) < new Date();
                return (
                  <IndexTable.Row
                    id={String(r.id)}
                    key={r.id}
                    position={i}
                    onClick={() => {
                      if (!canEdit) return;
                      setError(null);
                      setEditing({
                        ...EMPTY,
                        ...Object.fromEntries(
                          Object.entries(r).map(([k, v]) => [k, v ?? (k === "active" ? true : "")]),
                        ),
                      });
                    }}
                  >
                    <IndexTable.Cell>
                      <InlineStack gap="200" blockAlign="center">
                        <Text fontWeight="semibold">{r.name}</Text>
                        {!r.active && <Badge>Inactivo</Badge>}
                      </InlineStack>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{r.phone || "—"}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {r.license_expires ? (
                        <Badge tone={licExpired ? "critical" : undefined}>
                          {`${licExpired ? "Vencida" : "Vence"} ${formatDate(r.license_expires)}`}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>{r.banco || "—"}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {r.clabe ? (
                        <ClabeBadge status={r.bank.clabe_status} holderName={r.bank.holder_name} />
                      ) : (
                        <Text tone="subdued">Sin CLABE</Text>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text alignment="end">{r.trips}</Text>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          </Card>
        </BlockStack>

        <Modal
          open={!!editing}
          onClose={() => setEditing(null)}
          title={editing?.id ? editing.name : "Nuevo trasladista"}
          primaryAction={{
            content: "Guardar",
            loading: saving,
            disabled: !editing?.name?.trim() || !!clabeError,
            onAction: save,
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setEditing(null) }]}
        >
          {editing && (
            <Modal.Section>
              <FormLayout>
                {error && <Banner tone="critical">{error}</Banner>}
                <TextField
                  label="Nombre completo"
                  autoComplete="off"
                  value={editing.name}
                  onChange={set("name")}
                  helpText="Tal como aparece en su cuenta bancaria: el banco lo compara con el titular."
                />
                <FormLayout.Group>
                  <TextField label="Teléfono" type="tel" autoComplete="off" value={editing.phone} onChange={set("phone")} />
                  <TextField label="Correo" type="email" autoComplete="off" value={editing.email} onChange={set("email")} />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField label="RFC" autoComplete="off" value={editing.rfc} onChange={set("rfc")} />
                  <TextField label="CURP" autoComplete="off" value={editing.curp} onChange={set("curp")} />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    label="Número de licencia"
                    autoComplete="off"
                    value={editing.license_number}
                    onChange={set("license_number")}
                  />
                  <TextField
                    label="Vigencia de licencia"
                    type="date"
                    autoComplete="off"
                    value={editing.license_expires}
                    onChange={set("license_expires")}
                    error={expiredLicense ? "La licencia está vencida" : undefined}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    label="CLABE"
                    autoComplete="off"
                    inputMode="numeric"
                    value={editing.clabe}
                    onChange={(v) => set("clabe")(clabeDigits(v).slice(0, 18))}
                    error={clabeError}
                    helpText="Al guardar se verifica con el banco (1–2 min)."
                  />
                  <TextField label="Banco" value={clabe ? clabeBankName(clabe) || "—" : "—"} readOnly autoComplete="off" />
                </FormLayout.Group>
                {editingRow?.clabe && (
                  <InlineStack gap="200" blockAlign="center">
                    <ClabeBadge
                      status={editingRow.bank.clabe_status}
                      holderName={editingRow.bank.holder_name}
                    />
                    {editingRow.bank.holder_name && (
                      <Text tone="subdued" variant="bodySm">
                        Titular según el banco: {editingRow.bank.holder_name}
                      </Text>
                    )}
                    {["failed", "name_mismatch", "rfc_mismatch", "unverified"].includes(
                      editingRow.bank.clabe_status,
                    ) && (
                      <Button variant="plain" onClick={() => reverify(editingRow.id)}>
                        Verificar de nuevo
                      </Button>
                    )}
                  </InlineStack>
                )}
                <TextField label="Notas" multiline={2} autoComplete="off" value={editing.notes} onChange={set("notes")} />
                <Checkbox label="Activo" checked={editing.active} onChange={set("active")} />
              </FormLayout>
            </Modal.Section>
          )}
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default Trasladistas;
